import { useCallback } from "react";

import { Run, RunState, RunType, Stack } from "types/generated";
import { AccessResult } from "utils/access";

import useCanCreateTask from "./useCanCreateTask";

/**
 * Hook that returns a function to check if a task can be performed again (history run)
 *
 * @returns A function that takes a run and stack and returns an AccessResult
 * indicating whether the task can be performed again and any error message if it cannot
 */
const useCanPerformTaskAgain = (): ((run: Run, stack: Stack) => AccessResult) => {
  const canCreateTask = useCanCreateTask();

  return useCallback(
    (run: Run, stack: Stack) => {
      const canPerformAgain =
        run.type === RunType.Task &&
        (run.state === RunState.Failed ||
          run.state === RunState.Discarded ||
          run.state === RunState.Stopped ||
          run.state === RunState.Finished);

      if (!canPerformAgain) {
        return {
          result: false,
        };
      }

      return canCreateTask(stack);
    },
    [canCreateTask]
  );
};

export default useCanPerformTaskAgain;
